import { Transaction, FxSource } from '../types';
import { audPerUsdSync, carryForward, lookupRatesSync, todayIso } from './fxRates';
import type { DatedRates } from './fxRates';

/**
 * Simplified Australian CGT engine (estimate only — not tax advice).
 *
 * - Cost basis via FIFO (first in, first out): each SELL is matched against the
 *   earliest-acquired BUY parcels first, across the coin's FULL history (a sell
 *   this FY can consume parcels bought in prior years).
 * - A single sell may split into several sub-disposals (one per consumed parcel),
 *   so each carries its own acquisition date / holding period for discount treatment.
 * - TRANSFERs are not CGT events and produce no disposal, but their network fee
 *   IS picked up as a cost base element — see the fees note below.
 * - 50% CGT discount applies only to GAINS on parcels held MORE THAN 12 MONTHS
 *   (a genuine calendar comparison — see `heldMoreThan12Months`, not a day count).
 * - Losses offset gross gains BEFORE the discount (ATO order); non-discounted gains
 *   are offset first so the more valuable discounted gains are preserved.
 *
 * ── THE ENGINE WORKS IN AUD ───────────────────────────────────────────────────
 *
 * Every monetary field on `Disposal` and `FYCgtResult` is AUD, converted PER LEG
 * at the rate for that leg's OWN date. This is the substantive change from the
 * engine's earlier shape, which computed in stored USD and let each render site
 * multiply by one live AUD rate at the end.
 *
 * That old shape was wrong in a way that got worse the longer a parcel was held:
 * a 2021 buy and a 2025 sell were converted at the SAME rate — today's — so the
 * AUD/USD movement between them silently vanished from the gain. An Australian
 * CGT return needs each leg at its own date's rate, because the FX movement
 * between acquisition and disposal is itself part of the Australian-dollar gain.
 *
 * Rates come, per leg, from:
 *   1. the transaction's own stored `fxRateAud`, when it was resolved for the
 *      transaction's date (`fxSource` of rba/ecb/manual);
 *   2. otherwise the AUD/USD rate for that date (`audPerUsdSync` — bundled RBA
 *      table, then the cached ECB feed) applied to the stored USD price. A date
 *      after the bundle's end comes from ECB only, never from the bundle's last
 *      rate (see lookupRatesSync);
 *   3. otherwise the most recent EARLIER known rate (`carryForward`), with the
 *      leg flagged `source: 'carried'`;
 *   4. otherwise the caller's live spot rate, flagged `source: 'spot'`.
 * Both 3 and 4 mark the disposal `fxEstimated` so the UI says it is approximate.
 *
 * Because leg 2 is synchronous and cache-backed, callers must WARM the cache for
 * the dates in play (`warmRatesForDates`) before relying on it — otherwise every
 * uncovered date silently degrades to leg 3.
 *
 * ── FEES ──────────────────────────────────────────────────────────────────────
 *
 * Three kinds of fee reach this engine, and they are NOT treated alike:
 *
 *   BUY fee      → joins the cost base (an incidental cost of acquisition).
 *   SELL fee     → reduces proceeds (an incidental cost of disposal).
 *   TRANSFER fee → joins the cost base of the parcels open when it was paid.
 *
 * All three reduce the taxable gain; the first two are uncontroversial.
 *
 * The transfer case is a judgement call, so it is stated plainly. Moving a coin
 * between your own wallets is not a disposal, so the fee is not an incidental
 * cost of one. It is a cost of OWNING the asset, which the ATO's cost base has
 * its own element for — but that element is only available where the cost is not
 * otherwise deductible, and for an investor holding on capital account that is
 * arguable either way. This engine takes the cost base reading, for two reasons:
 * it is the treatment the portfolio side already applies (`computeHoldings`), so
 * the two cost bases now agree instead of quietly differing; and it is the
 * conservative direction, deferring the benefit to disposal rather than claiming
 * it immediately. Anyone whose adviser prefers the deduction reading can take it
 * separately — the fee is reported per disposal (`acquisitionFeeAud`) rather than
 * silently folded in, so it can be backed out.
 *
 * Fees are FIAT ONLY. A fee paid in the asset itself is not supported, because
 * valuing it needs the coin's own price on the transaction date — a second price
 * lookup, not an FX conversion.
 *
 * A fee is one real amount that may have to spread over several FIFO parcels.
 * `allocateProportionally` splits it so the shares sum back to the whole fee
 * exactly, because a fee the taxpayer paid and the report cannot account for is
 * a defect, not a rounding detail.
 *
 * ── ROUNDING ──────────────────────────────────────────────────────────────────
 *
 * Per-transaction rounding to the nearest cent, applied ONCE, here. See
 * `roundAud` for the full rationale. Every consumer — screen, CSV, PDF — reads
 * these already-rounded numbers and must not re-round or re-derive them, which
 * is what keeps the three outputs identical to the cent.
 *
 * The PORTFOLIO side deliberately does NOT round (see the matching note in
 * calculations.ts). These figures get filed and must reconcile against each other
 * exactly; portfolio figures are indicative and recomputed on every price tick,
 * where per-transaction rounding would only make totals jitter. The divergence is
 * intentional — do not "harmonise" the two.
 */

// AU resident marginal brackets (mirrors the per-coin Tax estimator's brackets).
export const TAX_BRACKETS = [
  { label: '$0 – $18,200',        rate: 0      },
  { label: '$18,201 – $45,000',   rate: 0.19   },
  { label: '$45,001 – $120,000',  rate: 0.325  },
  { label: '$120,001 – $180,000', rate: 0.37   },
  { label: '$180,001+',           rate: 0.45   },
];
export const DEFAULT_BRACKET_IDX = 2; // $45,001–$120,000 (32.5%)

// ── Money: one rounding policy, applied once ─────────────────────────────────

/**
 * Round an AUD amount to the nearest cent, half away from zero.
 *
 * POLICY: per-transaction rounding to two decimal places. The ATO's guidance is
 * to convert and round each transaction on its own, then work with those
 * figures — not to carry full precision through and round the totals at the end.
 * The two disagree by cents on a busy year, and only the former reconciles
 * against the per-disposal rows a taxpayer actually files.
 *
 * `Math.round` alone is not this function: it rounds HALF UP toward positive
 * infinity, so −0.125 → −0.12 while +0.125 → +0.13. Capital losses would round
 * a cent kinder than capital gains, which is both asymmetric and indefensible.
 * Rounding the magnitude and reapplying the sign treats them alike.
 *
 * The `1e-9` nudge absorbs binary-float representation error (0.145 is stored
 * fractionally below 0.145, and would otherwise round down). It is far smaller
 * than a cent, so it can never move a genuinely mid-value figure.
 */
export function roundAud(n: number): number {
  if (!isFinite(n)) return 0;
  const sign = n < 0 ? -1 : 1;
  return (sign * Math.round(Math.abs(n) * 100 + 1e-9)) / 100;
}

/**
 * Sum AUD amounts that are ALREADY rounded to cents, without reintroducing
 * float drift.
 *
 * Adding 2dp floats is not exact (0.1 + 0.2 = 0.30000000000000004), and an FY
 * with a few hundred disposals accumulates enough of that to show up as a
 * one-cent disagreement between the on-screen total and the sum of the exported
 * rows. Summing whole cents as integers and dividing once at the end cannot
 * drift at all.
 */
export function sumAud(values: number[]): number {
  let cents = 0;
  for (const v of values) cents += Math.round(v * 100);
  return cents / 100;
}

/**
 * Split `total` across `weights` so the parts sum back to `total` EXACTLY.
 *
 * Used for fees, which are single real-world amounts that have to be spread over
 * however many FIFO parcels a trade happens to touch. Rounding each share
 * independently does not work: a $10.00 fee over three equal parcels gives
 * 3 × $3.33 = $9.99, and the missing cent is a fee the taxpayer paid and the
 * report does not show.
 *
 * So: allocate whole cents by the largest-remainder method. Each share gets the
 * floor of its exact proportion, then the leftover cents go to the shares with
 * the largest fractional parts (ties to the earlier index, so the result is
 * deterministic and not dependent on sort stability). The parts are therefore
 * within one cent of exact proportionality AND sum to the total.
 */
export function allocateProportionally(total: number, weights: number[]): number[] {
  const n = weights.length;
  if (n === 0) return [];

  const signedCents = Math.round(total * 100);
  const sign = signedCents < 0 ? -1 : 1;
  const totalCents = Math.abs(signedCents);

  // Negative weights are meaningless here, and a zero total or zero weight-sum
  // has nothing to distribute. Returning zeros beats dividing by zero.
  const w = weights.map((x) => (isFinite(x) && x > 0 ? x : 0));
  const sumW = w.reduce((s, x) => s + x, 0);
  if (totalCents === 0 || !(sumW > 0)) return new Array(n).fill(0);

  const exact = w.map((x) => (totalCents * x) / sumW);
  const cents = exact.map((e) => Math.floor(e));
  let leftover = totalCents - cents.reduce((s, c) => s + c, 0);

  const byFrac = exact
    .map((e, i) => ({ i, frac: e - Math.floor(e) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);

  for (let k = 0; k < byFrac.length && leftover > 0; k++) {
    cents[byFrac[k].i] += 1;
    leftover--;
  }

  return cents.map((c) => (sign * c) / 100);
}

// ── FX per leg ───────────────────────────────────────────────────────────────

/**
 * Where a leg's AUD rate came from. `carried` and `spot` both mean no rate was
 * published for the leg's date: `carried` used the last earlier one, `spot`
 * today's. Either makes the figure approximate (isEstimatedLeg).
 */
export type LegFxSource = Exclude<FxSource, 'spot-at-entry'> | 'carried' | 'spot';

/** True for a leg whose rate is an estimate rather than the date's own. */
export function isEstimatedLeg(source: LegFxSource): boolean {
  return source === 'carried' || source === 'spot';
}

/** The rate actually applied to one leg (one BUY parcel, or one SELL). */
export interface LegFx {
  /** AUD per 1 unit of `currency`. */
  rate: number;
  /** The currency the leg's price is denominated in before conversion. */
  currency: string;
  /** The date `rate` is for — may be rolled back from the leg's own date. */
  rateDate: string;
  source: LegFxSource;
}

export interface CgtFxOptions {
  /**
   * Last-resort AUD per 1 USD, used only for legs with no date-correct rate.
   * Callers pass today's live rate; legs that use it are flagged `spot`.
   */
  spotAudPerUsd: number;
  /**
   * Date → AUD per USD. Defaults to the bundled-RBA/cached-ECB lookup;
   * overridable so tests can pin rates without touching localStorage.
   */
  audPerUsdOn?: (date: string) => { rate: number; rateDate: string; source: 'rba' | 'ecb' } | null;
  /**
   * (currency, date) → that currency's rates. Used to re-resolve a fee from its
   * RAW stored amount, which is why the raw amount is never overwritten.
   * Defaults to the same bundled lookup; overridable for tests.
   */
  ratesOn?: (currency: string, date: string) => DatedRates | null;
}

/** Date-correct sources. A `spot-at-entry` row's rates are today's, so not these. */
function isDateCorrect(source: FxSource | undefined): source is Exclude<FxSource, 'spot-at-entry'> {
  return source === 'rba' || source === 'ecb' || source === 'manual';
}

function positive(n: number | undefined): n is number {
  return typeof n === 'number' && isFinite(n) && n > 0;
}

/**
 * One transaction's price per unit, in AUD, plus the rate that produced it.
 *
 * Preference order matches the module doc: the row's own date-correct rate, then
 * the AUD/USD rate for the row's date, then the caller's spot rate.
 */
export function priceAudFor(
  tx: Pick<Transaction, 'price' | 'date' | 'entryAmount' | 'entryCurrency' | 'fxRateAud' | 'fxRateDate' | 'fxSource'>,
  opts: CgtFxOptions
): { priceAud: number; fx: LegFx } {
  // 1. The row resolved its own rate for its own date — the best evidence there
  //    is, and the only path that avoids a second conversion through USD.
  if (
    isDateCorrect(tx.fxSource) &&
    positive(tx.fxRateAud) &&
    positive(tx.entryAmount) &&
    tx.entryCurrency
  ) {
    return {
      priceAud: tx.entryAmount * tx.fxRateAud,
      fx: {
        rate: tx.fxRateAud,
        currency: tx.entryCurrency,
        rateDate: tx.fxRateDate ?? tx.date,
        source: tx.fxSource,
      },
    };
  }

  // 2. Legacy rows (and spot-at-entry rows, whose stored rate is today's, not
  //    their date's): convert the stored USD price at that date's AUD/USD rate.
  const lookup = opts.audPerUsdOn ?? audPerUsdSync;
  const dated = lookup(tx.date);
  if (dated && positive(dated.rate)) {
    return {
      priceAud: tx.price * dated.rate,
      fx: { rate: dated.rate, currency: 'USD', rateDate: dated.rateDate, source: dated.source },
    };
  }

  // 3. Nothing published for the date (ECB unreachable when warming): the last
  //    known earlier rate, flagged. The same rule the display tables use.
  const carried = carryForward(tx.date, lookup);
  if (carried && positive(carried.value.rate)) {
    return {
      priceAud: tx.price * carried.value.rate,
      fx: { rate: carried.value.rate, currency: 'USD', rateDate: carried.value.rateDate, source: 'carried' },
    };
  }

  // 4. Nothing at all. Use the live rate and SAY SO, rather than reporting an
  //    approximation as though it were the real figure.
  const spot = positive(opts.spotAudPerUsd) ? opts.spotAudPerUsd : 1;
  return {
    priceAud: tx.price * spot,
    fx: { rate: spot, currency: 'USD', rateDate: todayIso(), source: 'spot' },
  };
}

/** A fee converted to AUD, and whether that conversion had to be approximated. */
export interface FeeAud {
  feeAud: number;
  estimated: boolean;
}

const NO_FEE: FeeAud = { feeAud: 0, estimated: false };

/**
 * A BUY/SELL transaction fee in AUD, at the rate for the transaction's own date.
 *
 * Preference order mirrors `priceAudFor`, with one addition: the RAW `fee` +
 * `feeCurrency` can be re-resolved directly, which is the whole reason they are
 * never overwritten with a converted value. That path is preferred over the
 * stored `feeUsd`, because going through USD is a second conversion.
 *
 * An absent fee is ZERO, not an error — that is what makes every pre-fee row
 * produce exactly the numbers it produced before fees existed.
 */
export function feeAudFor(
  tx: Pick<Transaction, 'date' | 'fee' | 'feeCurrency' | 'feeUsd' | 'feeAud' | 'fxSource'>,
  opts: CgtFxOptions
): FeeAud {
  // 1. Already converted for this row's own date.
  if (isDateCorrect(tx.fxSource) && positive(tx.feeAud)) {
    return { feeAud: tx.feeAud, estimated: false };
  }

  const rawFee = positive(tx.fee) ? tx.fee : undefined;

  // 2. Re-resolve from the raw amount and its currency.
  if (rawFee != null && tx.feeCurrency) {
    const lookup = opts.ratesOn ?? lookupRatesSync;
    const rates = lookup(tx.feeCurrency, tx.date);
    if (rates && positive(rates.fxRateAud)) {
      return { feeAud: rawFee * rates.fxRateAud, estimated: false };
    }
    // Nothing published for the date: the last known earlier rate, flagged.
    const feeCurrency = tx.feeCurrency;
    const carried = carryForward(tx.date, (d) => {
      const r = lookup(feeCurrency, d);
      return r && positive(r.fxRateAud) ? r : null;
    });
    if (carried && positive(carried.value.fxRateAud)) {
      return { feeAud: rawFee * carried.value.fxRateAud, estimated: true };
    }
  }

  // 3. Via the stored USD cache, at that date's AUD/USD rate.
  const audPerUsd = opts.audPerUsdOn ?? audPerUsdSync;
  if (positive(tx.feeUsd)) {
    const dated = audPerUsd(tx.date);
    if (dated && positive(dated.rate)) {
      return { feeAud: tx.feeUsd * dated.rate, estimated: false };
    }
    const carried = carryForward(tx.date, audPerUsd);
    if (carried && positive(carried.value.rate)) {
      return { feeAud: tx.feeUsd * carried.value.rate, estimated: true };
    }
    const spot = positive(opts.spotAudPerUsd) ? opts.spotAudPerUsd : 1;
    return { feeAud: tx.feeUsd * spot, estimated: true };
  }

  // 4. A raw fee with no usable rate anywhere. A USD fee is still exactly itself
  //    in USD, so it can go through the AUD/USD leg; anything else is dropped
  //    rather than guessed, and the disposal is flagged.
  if (rawFee != null) {
    if ((tx.feeCurrency ?? 'USD').toUpperCase() === 'USD') {
      const dated = audPerUsd(tx.date);
      const carried = dated ? null : carryForward(tx.date, audPerUsd);
      const rate = dated && positive(dated.rate)
        ? dated.rate
        : carried && positive(carried.value.rate)
        ? carried.value.rate
        : positive(opts.spotAudPerUsd) ? opts.spotAudPerUsd : 1;
      return { feeAud: rawFee * rate, estimated: !dated };
    }
    return { feeAud: 0, estimated: true };
  }

  return NO_FEE;
}

/**
 * A TRANSFER's network fee in AUD.
 *
 * Transfers predate the fee model above and store `networkFee` already converted
 * to USD. The raw amount as typed is recoverable as `networkFee / fxRateUsd`
 * (which is how it was written), so a row with date-correct rates converts
 * without a second trip through USD; otherwise the USD figure is converted at the
 * transfer date's AUD/USD rate.
 */
export function transferFeeAudFor(
  tx: Pick<Transaction, 'date' | 'networkFee' | 'fxRateUsd' | 'fxRateAud' | 'fxSource'>,
  opts: CgtFxOptions
): FeeAud {
  if (!positive(tx.networkFee)) return NO_FEE;

  if (isDateCorrect(tx.fxSource) && positive(tx.fxRateUsd) && positive(tx.fxRateAud)) {
    const rawFee = tx.networkFee / tx.fxRateUsd;
    return { feeAud: rawFee * tx.fxRateAud, estimated: false };
  }

  const audPerUsd = opts.audPerUsdOn ?? audPerUsdSync;
  const dated = audPerUsd(tx.date);
  if (dated && positive(dated.rate)) {
    return { feeAud: tx.networkFee * dated.rate, estimated: false };
  }

  const carried = carryForward(tx.date, audPerUsd);
  if (carried && positive(carried.value.rate)) {
    return { feeAud: tx.networkFee * carried.value.rate, estimated: true };
  }

  const spot = positive(opts.spotAudPerUsd) ? opts.spotAudPerUsd : 1;
  return { feeAud: tx.networkFee * spot, estimated: true };
}

export interface Disposal {
  sellId: string;
  coinId: string;
  symbol: string;
  name: string;
  quantity: number;          // matched quantity for this sub-disposal
  /**
   * AUD, rounded: quantity × sell price at the sell date's rate, NET of this
   * sub-disposal's share of the sell fee. Gross is `proceeds + disposalFeeAud`.
   */
  proceeds: number;
  /**
   * AUD, rounded: quantity × parcel buy price at the BUY date's rate, PLUS this
   * sub-disposal's share of the acquisition fee. Gross is
   * `costBasis − acquisitionFeeAud`.
   */
  costBasis: number;
  gain: number;              // AUD: proceeds − costBasis (negative = loss)
  /**
   * AUD share of the acquisition-side fee included in `costBasis` — the BUY's own
   * fee, plus any TRANSFER network fee attributed to this parcel. Shares across
   * all sub-disposals that consume a parcel sum to that parcel's whole fee.
   */
  acquisitionFeeAud: number;
  /**
   * AUD share of the SELL's fee netted off `proceeds`. Shares across the
   * sub-disposals of one sell sum to that sell's whole fee, exactly.
   */
  disposalFeeAud: number;
  discountAmount: number;    // AUD: 50% of the gain when discount-eligible, else 0
  taxableGain: number;       // AUD: gain after the discount; 0 for a loss
  acquisitionDate: string;   // parcel buy date (ISO)
  disposalDate: string;      // sell date (ISO)
  disposalTs: number;        // ms parsed from disposalDate
  heldDays: number;          // whole calendar days, acquisition → disposal
  discountEligible: boolean; // held more than 12 months && gain > 0
  wallet?: string;           // wallet the coins were sold from (passthrough, if set)
  acquisitionFx: LegFx;      // rate applied to the cost-basis leg
  disposalFx: LegFx;         // rate applied to the proceeds leg
  /** True when either leg fell back to the live spot rate — figure is approximate. */
  fxEstimated: boolean;
}

/**
 * A SELL that FIFO could not fully match against acquired parcels.
 *
 * The engine disposes only the quantity that has a cost basis and drops the rest
 * — it cannot invent an acquisition price for coins with no recorded purchase.
 * That is the right calculation, but it used to happen in silence, so a ledger
 * with a missing or mis-dated BUY produced a quietly understated gain with
 * nothing on screen to say so.
 *
 * This record does not change any figure. It exists so the Tax tab can tell the
 * user which disposal is incomplete and by how much.
 */
export interface UnmatchedDisposal {
  sellId: string;
  coinId: string;
  symbol: string;
  name: string;
  /** The sell's own date (ISO). */
  date: string;
  /** ms parsed from `date`, for FY filtering — same basis as `Disposal.disposalTs`. */
  disposalTs: number;
  /** Quantity sold that no parcel could cover. */
  quantity: number;
  /** Quantity that DID match. Zero when nothing did, so no disposal exists at all. */
  matchedQuantity: number;
}

/** A disposal with the marginal-rate-dependent figure `calculateFYCGT` can add. */
export interface TaxedDisposal extends Disposal {
  /**
   * AUD tax on THIS disposal in isolation, at the selected marginal rate —
   * before capital losses elsewhere in the FY are offset. Deliberately does not
   * sum to `estCGT`; the summary applies the ATO order of operations and these
   * rows do not.
   */
  cgtOwed: number;
}

export interface FYCgtResult {
  disposalsInFY: TaxedDisposal[];
  totalGains: number;            // AUD gross capital gains (FY)
  totalLosses: number;           // AUD gross capital losses (FY, positive)
  grossCapitalGain: number;      // after losses, BEFORE the discount (nonDiscAfter + discAfter)
  discountApplied: number;       // AUD value of the 50% discount subtracted
  netCapitalGain: number;        // after losses + discount (≥ 0)
  taxableGain: number;           // = netCapitalGain
  estCGT: number;                // taxableGain × marginalRate
  netLossCarriedForward: number; // > 0 when losses exceed gains
  /** True when any disposal in the FY used a spot-rate fallback on either leg. */
  fxEstimated: boolean;

  // ── Reconciliation ────────────────────────────────────────────────────────
  // How the disposal COUNT breaks down, so "12 disposals" can be squared against
  // the two money totals. These three always sum to disposalsInFY.length.
  /** Disposals with a gain strictly above zero. */
  gainCount: number;
  /** Disposals with a loss. */
  lossCount: number;
  /**
   * Disposals that broke exactly even. Counted separately rather than folded into
   * `gainCount`, because a reader reconciling the rows needs somewhere for them
   * to go — they are why the count and the totals used to appear to disagree.
   */
  breakEvenCount: number;

  // ── Incomplete matches ────────────────────────────────────────────────────
  /** SELLs in this FY that FIFO could not fully cover. Empty in the normal case. */
  unmatchedInFY: UnmatchedDisposal[];
  /** Convenience for the UI: `unmatchedInFY.length > 0`. */
  hasUnmatched: boolean;
}

const EPS = 1e-9;
const MS_PER_DAY = 86_400_000;

// Parse a 'YYYY-MM-DD' date as LOCAL midnight, so it lines up with the local-time
// FY bounds (a bare ISO date would otherwise parse as UTC and drift by the offset).
// Used for transaction ordering and for `disposalTs`, which is compared against
// the local-time FY bounds — NOT for holding periods, which are calendar-based.
function dateMs(iso: string): number {
  const t = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).getTime();
  return isFinite(t) ? t : 0;
}

/** The calendar parts of a 'YYYY-MM-DD' (or a longer ISO string starting with one). */
function dateParts(iso: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

/**
 * Days since the epoch for a calendar date, computed from the date PARTS via
 * Date.UTC.
 *
 * UTC has no daylight saving, so a difference of two of these is always a whole
 * number of days. Subtracting two LOCAL midnights instead — which is what this
 * engine used to do — drifts by an hour whenever the span crosses a DST
 * transition, producing holding periods like 181.0416 days.
 */
function dayNumber(iso: string): number {
  const p = dateParts(iso);
  if (!p) return NaN;
  return Math.floor(Date.UTC(p.y, p.m - 1, p.d) / MS_PER_DAY);
}

/** Whole calendar days from `from` to `to`, never negative. */
function calendarDaysBetween(from: string, to: string): number {
  const a = dayNumber(from);
  const b = dayNumber(to);
  if (!isFinite(a) || !isFinite(b)) return 0;
  return Math.max(0, b - a);
}

/**
 * Whether the ATO's 50% discount holding requirement is met: MORE THAN 12
 * MONTHS, which is not the same as more than 365 days.
 *
 * Across a leap year the two diverge — 1 May 2023 → 1 May 2024 is 366 days but
 * is exactly 12 months, and is NOT eligible. Eligibility starts the day AFTER
 * the anniversary of the acquisition date.
 *
 * A 29 February acquisition has no anniversary in a non-leap year. Date.UTC
 * rolls 29 Feb forward to 1 March, so eligibility starts 2 March — the
 * conservative reading (later eligibility, so the discount is never granted
 * early).
 */
function heldMoreThan12Months(acquired: string, disposed: string): boolean {
  const a = dateParts(acquired);
  if (!a) return false;
  const disposedDay = dayNumber(disposed);
  if (!isFinite(disposedDay)) return false;
  const anniversary = Math.floor(Date.UTC(a.y + 1, a.m - 1, a.d) / MS_PER_DAY);
  return disposedDay > anniversary;
}

/**
 * FIFO-match every SELL against prior BUY parcels → all sub-disposals (not
 * FY-filtered), with every figure converted to AUD at its own leg's rate.
 */
export function matchDisposalsWithDiagnostics(
  transactions: Transaction[],
  fxOptions: CgtFxOptions
): { disposals: Disposal[]; unmatched: UnmatchedDisposal[] } {
  const byCoin = new Map<string, Transaction[]>();
  for (const tx of transactions) {
    // TRANSFERs are still not disposals, but they are no longer discarded here:
    // a transfer's network fee is a cost of holding the asset, and it has to be
    // attributed to the parcels open AT THE TIME of the transfer, which means
    // seeing it in date order alongside the buys and sells.
    if (tx.type !== 'BUY' && tx.type !== 'SELL' && tx.type !== 'TRANSFER') continue;
    const list = byCoin.get(tx.coinId);
    if (list) list.push(tx); else byCoin.set(tx.coinId, [tx]);
  }

  const disposals: Disposal[] = [];
  const unmatched: UnmatchedDisposal[] = [];

  byCoin.forEach((txs) => {
    // Acquisition order: by entered date, tie-broken by insertion timestamp.
    const ordered = [...txs].sort((a, b) => {
      const da = dateMs(a.date), db = dateMs(b.date);
      return da !== db ? da - db : a.timestamp - b.timestamp;
    });

    /**
     * Each parcel carries the AUD price and the rate that produced it, resolved
     * once when the BUY is seen. A parcel consumed by three later sells is
     * converted at ITS OWN acquisition-date rate all three times — that is the
     * whole point of holding the rate on the parcel rather than at the sell.
     *
     * `feeAud` is a POOL that shrinks as the parcel is consumed: it starts as the
     * BUY's own fee and grows when a transfer fee is attributed to this parcel.
     * Holding it per parcel is what keeps a partly-sold parcel's unsold fee in
     * the cost base of what is still held, rather than deducting it early.
     */
    const parcels: {
      qty: number;
      priceAud: number;
      date: string;
      fx: LegFx;
      feeAud: number;
      feeEstimated: boolean;
    }[] = [];

    for (const tx of ordered) {
      if (tx.type === 'BUY') {
        const { priceAud, fx } = priceAudFor(tx, fxOptions);
        const fee = feeAudFor(tx, fxOptions);
        parcels.push({
          qty: tx.amount,
          priceAud,
          date: tx.date,
          fx,
          feeAud: roundAud(fee.feeAud),
          feeEstimated: fee.estimated,
        });
        continue;
      }

      if (tx.type === 'TRANSFER') {
        // Not a CGT event, and not a disposal — but the network fee is a real
        // cost of holding the asset, so it is spread across the parcels open at
        // this moment, in proportion to what is left of each. See the module
        // header note on transfer fees for why they are treated as cost base.
        const fee = transferFeeAudFor(tx, fxOptions);
        if (fee.feeAud > 0 && parcels.length > 0) {
          const shares = allocateProportionally(fee.feeAud, parcels.map((p) => p.qty));
          parcels.forEach((p, i) => {
            p.feeAud = roundAud(p.feeAud + shares[i]);
            if (fee.estimated) p.feeEstimated = true;
          });
        }
        // A transfer fee with no open parcel has no asset to attach to and is
        // dropped. Paying to move a coin you hold none of is already degenerate;
        // inventing a disposal to hang it on would be worse.
        continue;
      }

      // SELL — matched against parcels FIFO, in two passes.
      const sellTs = dateMs(tx.date);
      const { priceAud: sellPriceAud, fx: disposalFx } = priceAudFor(tx, fxOptions);
      const sellFee = feeAudFor(tx, fxOptions);

      // Pass 1: consume parcels, recording what each contributes. The parcel's
      // fee share is taken here because it depends on the parcel's own remaining
      // pool, which only exists while it is being consumed.
      const matched: {
        q: number;
        parcelPriceAud: number;
        parcelDate: string;
        parcelFx: LegFx;
        acquisitionFeeAud: number;
        feeEstimated: boolean;
      }[] = [];

      let remaining = tx.amount;
      while (remaining > EPS && parcels.length > 0) {
        const p = parcels[0];
        const q = Math.min(remaining, p.qty);
        const fullyConsumed = q >= p.qty - EPS;

        // Taking the WHOLE remaining pool on full consumption is what guarantees
        // a parcel's fee is neither lost nor duplicated across however many
        // sells eat it: the shares are exact by construction, not by rounding.
        const acquisitionFeeAud = fullyConsumed ? p.feeAud : roundAud((p.feeAud * q) / p.qty);
        p.feeAud = fullyConsumed ? 0 : roundAud(p.feeAud - acquisitionFeeAud);

        matched.push({
          q,
          parcelPriceAud: p.priceAud,
          parcelDate: p.date,
          parcelFx: p.fx,
          acquisitionFeeAud,
          feeEstimated: p.feeEstimated,
        });

        p.qty -= q;
        remaining -= q;
        if (p.qty <= EPS) parcels.shift();
      }

      // Sold more than was ever acquired (or acquired only AFTER this date). The
      // remainder has no cost basis, so it produces no disposal — but it is now
      // reported rather than dropped in silence.
      if (remaining > EPS) {
        unmatched.push({
          sellId: tx.id,
          coinId: tx.coinId,
          symbol: tx.symbol,
          name: tx.name,
          date: tx.date,
          disposalTs: sellTs,
          quantity: remaining,
          matchedQuantity: tx.amount - remaining,
        });
      }

      // Pass 2: the sell fee splits across the matched parcels in proportion to
      // quantity, summing back to the whole fee exactly.
      //
      // Note the denominator is the MATCHED quantity, not `tx.amount`. They differ
      // only when a sell exceeds everything ever acquired, which this engine
      // already handles by dropping the excess; allocating over the matched part
      // keeps the fee whole across the disposals that do exist, rather than
      // silently shaving it to match a quantity that produced no disposal.
      const disposalFeeShares = allocateProportionally(
        sellFee.feeAud,
        matched.map((m) => m.q)
      );

      matched.forEach((m, i) => {
        // Round once, here, per leg — the single place money becomes final.
        const grossProceeds = roundAud(m.q * sellPriceAud);
        const grossCostBasis = roundAud(m.q * m.parcelPriceAud);
        const disposalFeeAud = disposalFeeShares[i];

        // Acquisition fees join the cost base; disposal fees reduce proceeds.
        // Both therefore reduce the gain, which is the point.
        const costBasis = roundAud(grossCostBasis + m.acquisitionFeeAud);
        const proceeds = roundAud(grossProceeds - disposalFeeAud);
        // Both operands are exact cents, so the difference is too: no re-round.
        const gain = roundAud(proceeds - costBasis);

        const heldDays = calendarDaysBetween(m.parcelDate, tx.date);
        const discountEligible = heldMoreThan12Months(m.parcelDate, tx.date) && gain > 0;
        // A half-cent is possible here (an odd number of cents, halved), so this
        // is its own rounding step rather than a division left to each consumer.
        const discountAmount = discountEligible ? roundAud(gain * 0.5) : 0;
        const taxableGain = gain > 0 ? roundAud(gain - discountAmount) : 0;

        disposals.push({
          sellId: tx.id,
          coinId: tx.coinId,
          symbol: tx.symbol,
          name: tx.name,
          quantity: m.q,
          proceeds,
          costBasis,
          gain,
          acquisitionFeeAud: m.acquisitionFeeAud,
          disposalFeeAud,
          discountAmount,
          taxableGain,
          acquisitionDate: m.parcelDate,
          disposalDate: tx.date,
          disposalTs: sellTs,
          heldDays,
          discountEligible,
          wallet: tx.wallet,
          acquisitionFx: m.parcelFx,
          disposalFx,
          fxEstimated:
            isEstimatedLeg(m.parcelFx.source) ||
            isEstimatedLeg(disposalFx.source) ||
            m.feeEstimated ||
            sellFee.estimated,
        });
      });
    }
  });

  return { disposals, unmatched };
}

/**
 * FIFO-match every SELL against prior BUY parcels → all sub-disposals.
 *
 * The plain form, for callers that only need the disposals. Anything that wants
 * to surface incomplete matches calls `matchDisposalsWithDiagnostics` instead.
 */
export function matchDisposalsFIFO(
  transactions: Transaction[],
  fxOptions: CgtFxOptions
): Disposal[] {
  return matchDisposalsWithDiagnostics(transactions, fxOptions).disposals;
}

/** Apply the FY filter and ATO order-of-operations to produce the headline figures. */
export function calculateFYCGT(
  allDisposals: Disposal[],
  fyStartMs: number,
  fyEndMs: number,
  marginalRate: number,
  /**
   * Sells FIFO could not fully match, from `matchDisposalsWithDiagnostics`.
   * Optional and defaulted so callers that don't surface them are unaffected.
   */
  allUnmatched: UnmatchedDisposal[] = [],
): FYCgtResult {
  const inFY = allDisposals.filter(
    (d) => d.disposalTs >= fyStartMs && d.disposalTs <= fyEndMs
  );

  // Every per-disposal figure is already rounded to cents, so the aggregates are
  // integer-cent sums of them — never a re-derivation from unrounded values.
  // That is what makes the summary reconcile exactly against the exported rows.
  // `>= 0`, not `> 0`: a disposal that broke exactly even is a capital gain OF
  // ZERO, not an unclassified third thing. Adding zero cannot move the total, so
  // this changes no figure — it closes the gap where a disposal belonged to
  // neither pool and the count could not be reconciled. A break-even disposal is
  // never discount-eligible (that test is `gain > 0`), so it lands here.
  const nonDiscountedGains = sumAud(inFY.filter((d) => d.gain >= 0 && !d.discountEligible).map((d) => d.gain));
  const discountedGains = sumAud(inFY.filter((d) => d.gain > 0 && d.discountEligible).map((d) => d.gain));
  const losses = sumAud(inFY.filter((d) => d.gain < 0).map((d) => -d.gain));

  const totalGains = sumAud([nonDiscountedGains, discountedGains]);

  // Losses offset gross gains before the discount — non-discounted first.
  let lossPool = losses;
  const nonDiscAfter = Math.max(0, roundAud(nonDiscountedGains - lossPool));
  lossPool = roundAud(lossPool - (nonDiscountedGains - nonDiscAfter));
  const discAfter = Math.max(0, roundAud(discountedGains - lossPool));
  lossPool = roundAud(lossPool - (discountedGains - discAfter));

  const grossCapitalGain = sumAud([nonDiscAfter, discAfter]); // after losses, before the discount
  // Halving can land on a half-cent, so this rounds; netCapitalGain is then the
  // exact complement of the rounded discount, not a second independent halving.
  const discountApplied = roundAud(discAfter * 0.5);
  const netCapitalGain = roundAud(nonDiscAfter + (discAfter - discountApplied));
  const taxableGain = netCapitalGain;
  const estCGT = roundAud(taxableGain * marginalRate);
  const netLossCarriedForward = lossPool; // leftover losses = max(0, losses − gains)

  // The marginal rate is only known here, so the per-disposal tax figure is
  // attached here — rounded once, like everything else, so the exports can print
  // it without repeating the multiplication.
  const disposalsInFY: TaxedDisposal[] = inFY.map((d) => ({
    ...d,
    cgtOwed: roundAud(d.taxableGain * marginalRate),
  }));

  const unmatchedInFY = allUnmatched.filter(
    (u) => u.disposalTs >= fyStartMs && u.disposalTs <= fyEndMs
  );

  return {
    disposalsInFY,
    totalGains,
    totalLosses: losses,
    grossCapitalGain,
    discountApplied,
    netCapitalGain,
    taxableGain,
    estCGT,
    netLossCarriedForward,
    fxEstimated: inFY.some((d) => d.fxEstimated),
    gainCount: inFY.filter((d) => d.gain > 0).length,
    lossCount: inFY.filter((d) => d.gain < 0).length,
    breakEvenCount: inFY.filter((d) => d.gain === 0).length,
    unmatchedInFY,
    hasUnmatched: unmatchedInFY.length > 0,
  };
}

export interface FYBounds { startYear: number; endYear: number; startMs: number; endMs: number }

/** First-of-July → 30-June boundaries (ms) for the AU FY beginning in `startYear`. */
export function fyBoundsForStartYear(startYear: number): FYBounds {
  const endYear = startYear + 1;
  const startMs = new Date(startYear, 6, 1, 0, 0, 0, 0).getTime();         // 1 Jul
  const endMs = new Date(endYear, 5, 30, 23, 59, 59, 999).getTime();      // 30 Jun
  return { startYear, endYear, startMs, endMs };
}

/** First-of-July → 30-June boundaries (ms) for the AU FY that `today` falls in. */
export function auFinancialYearBounds(today: Date): FYBounds {
  const startYear = today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1;
  return fyBoundsForStartYear(startYear);
}

/** The AU FY start-year that a 'YYYY-MM-DD' (or ISO) date falls in. */
export function fyStartYearOfDate(iso: string): number {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (!isFinite(d.getTime())) return NaN;
  return d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
}
